import { GET_QUESTION_PROPERTY, GET_PUBLIC_QUESTION_PROPERTY } from '../../fetchTypes';
import {
  createDefaultRequestInitialState,
  createSelectorById,
  createSelectorByIdList,
  defaultRequestByIdReducer,
} from './withFetch';

const initialState = createDefaultRequestInitialState();
export const actionDataToId = (data) => data.qid;

// Qualtrics question property transformation function
const transformQualtricsQuestionProperty = (qualtricsQuestion) => {
  if (!qualtricsQuestion) return {};

  const questionProperties = {
    qid: qualtricsQuestion.QuestionID,
    title: qualtricsQuestion.DataExportTag || qualtricsQuestion.QuestionID.replace('QID', 'Q'),
    question: qualtricsQuestion.QuestionText || qualtricsQuestion.QuestionDescription || '',
    type: qualtricsQuestion.QuestionType,
    answeroptions: {},
    subquestions: {},
    attributes: {
      question_template: qualtricsQuestion.QuestionType,
      has_mean: qualtricsQuestion.QuestionType === 'Slider' ? '1' : '0',
      force_response: qualtricsQuestion.Validation?.Settings?.ForceResponse || 'OFF'
    },
    mandatory: qualtricsQuestion.Validation?.Settings?.ForceResponse === 'ON' ? 'Y' : 'N'
  };

  // Transform Choices to answeroptions
  if (qualtricsQuestion.Choices) {
    Object.entries(qualtricsQuestion.Choices).forEach(([key, choice]) => {
      questionProperties.answeroptions[key] = {
        code: key,
        answer: choice.Display,
        assessment_value: key, // Default assessment value
        scale_id: 0,
        order: qualtricsQuestion.ChoiceOrder?.indexOf(key) + 1 || parseInt(key)
      };
    });
  }

  // Transform Answers to answeroptions (for Matrix questions)
  if (qualtricsQuestion.Answers) {
    Object.entries(qualtricsQuestion.Answers).forEach(([key, answer]) => {
      questionProperties.answeroptions[key] = {
        code: key,
        answer: answer.Display,
        assessment_value: key,
        scale_id: 0,
        order: qualtricsQuestion.AnswerOrder?.indexOf(key) + 1 || parseInt(key)
      };
    });
  }

  // Transform subquestions for Matrix/SBS questions and MC+MAVR questions
  if (qualtricsQuestion.Choices && (['Matrix', 'SBS'].includes(qualtricsQuestion.QuestionType) ||
      (qualtricsQuestion.QuestionType === 'MC' && qualtricsQuestion.Selector === 'MAVR'))) {
    const questionTag = qualtricsQuestion.DataExportTag || qualtricsQuestion.QuestionID.replace('QID', 'Q');
    Object.entries(qualtricsQuestion.Choices).forEach(([key, choice]) => {
      questionProperties.subquestions[key] = {
        qid: `${qualtricsQuestion.QuestionID}_${key}`,
        code: key,
        title: `${questionTag}[${key}]`, // Use consistent naming pattern for all
        question: choice.Display,
        order: qualtricsQuestion.ChoiceOrder?.indexOf(parseInt(key)) + 1 || parseInt(key),
        scale_id: 0
      };
    });
  }

  // Handle AdditionalQuestions for SBS
  if (qualtricsQuestion.AdditionalQuestions) {
    Object.entries(qualtricsQuestion.AdditionalQuestions).forEach(([key, additionalQ]) => {
      const subQid = `${qualtricsQuestion.QuestionID}#${key}`;
      const questionTag = additionalQ.DataExportTag || qualtricsQuestion.QuestionID.replace('QID', 'Q');
      questionProperties.subquestions[subQid] = {
        qid: subQid,
        code: key,
        title: questionTag, // Use consistent naming pattern
        question: additionalQ.QuestionText,
        order: parseInt(key),
        scale_id: 0
      };
    });
  }

  // Special handling for Slider questions
  if (qualtricsQuestion.QuestionType === 'Slider') {
    questionProperties.attributes.slider_min = qualtricsQuestion.Configuration?.CSSliderMin || 0;
    questionProperties.attributes.slider_max = qualtricsQuestion.Configuration?.CSSliderMax || 100;
    questionProperties.attributes.slider_default = qualtricsQuestion.Configuration?.SliderStartPositions || {};
  }

  // Special handling for HeatMap questions
  if (qualtricsQuestion.QuestionType === 'HeatMap') {
    if (qualtricsQuestion.GraphicID) {
      // Create Qualtrics-specific URL format for HeatMap graphics
      questionProperties.attributes.image_path = `https://boltinsightltd.qualtrics.com/ControlPanel/Graphic.php?IM=${qualtricsQuestion.GraphicID}`;
    } else {
      questionProperties.attributes.image_path = '';
    }
    questionProperties.attributes.image_width = qualtricsQuestion.Width || 0;
    questionProperties.attributes.image_height = qualtricsQuestion.Height || 0;
  }

  return questionProperties;
};

const reducer = defaultRequestByIdReducer({
  actionDataToId,
  resultMapper: (data, action) => {
    // For Qualtrics, the question property might come from the questions API
    // In this case, we need to extract the specific question from the elements array
    if (data?.isQualtricsData && data?.questionData) {
      return transformQualtricsQuestionProperty(data.questionData);
    }
    
    // Default LimeSurvey format
    return (data || {}).response || {};
  },
});

export default (state = initialState, action) => {
  switch (action.fetchType || action.type) {
    case GET_QUESTION_PROPERTY:
      return reducer(state, action);
    case GET_PUBLIC_QUESTION_PROPERTY:
      return reducer(state, action);
    case 'SET_QUALTRICS_QUESTION_PROPERTY':
      // Directly set Qualtrics question property data
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.payload.qid]: {
            loading: false,
            loaded: true,
            result: action.payload.data,
            error: null,
            errorMessage: null
          }
        }
      };
    default:
      return state;
  }
};

const selfSelector = (state) => state.fetcher.questionProperty;
export const selectFetcherQuestionProperty = selfSelector;
export const selectQuestionPropertyByQid = createSelectorById(selfSelector);
export const selectQuestionPropertyListByQidList =
  createSelectorByIdList(selfSelector);
